import React, { useState } from 'react';
import { isForbidden } from '../../../../organisations/lib/errorMessage';
import { teacherAnalyticsAPI } from '../../../api/teacherAnalyticsAPI';
import { dayMonth } from './lib/activityFormat';

// The comment endpoint's limit (tracker comments are 1-5000 characters).
const COMMENT_MAX = 5000;

/** The comments on one piece, newest first, as the detail endpoint sent them. */
export const Comments = ({ comments, label }) => (comments.length ? (
  <ul className="td-act-comments" aria-label={label}>
    {comments.map((c) => (
      <li key={c.id}>
        <div className="td-stat__eyebrow td-act-label">{`${c.author_label} · ${dayMonth(c.created_at)}`}</div>
        <p className="td-act-comment">{c.text}</p>
      </li>
    ))}
  </ul>
) : null);

/** "Your comment on this <noun>": a textarea, Post and Cancel. */
export const Composer = ({ noun, posting, error, onSubmit, onCancel }) => {
  const [text, setText] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    if (await onSubmit(value)) setText('');
  };
  return (
    <form className="td-act-composer" onSubmit={submit}>
      <label className="td-field">
        <span>{`Your comment on this ${noun}`}</span>
        <textarea
          value={text}
          rows={3}
          maxLength={COMMENT_MAX}
          onChange={(e) => setText(e.target.value)}
          placeholder="Visible to the student and to other teachers in this group."
        />
      </label>
      {error ? <p className="td-error" role="alert">{error}</p> : null}
      <div className="td-act-btnrow">
        <button type="submit" className="td-btn td-btn--confirm td-btn--small" disabled={posting || !text.trim()}>
          {posting ? 'Posting…' : 'Post comment'}
        </button>
        <button type="button" className="td-btn td-btn--small" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

/**
 * The comments on one piece and posting a new one. `item` is the piece as
 * `{ kind, id }` (a subjects.SUBJECT_KINDS kind). A posted comment is added
 * to `comments` at once and passed to `onCommented(item, comment)`, so the
 * section and the Feedback tab can follow.
 */
export function usePieceComments(studentId, item, initial, onCommented) {
  const [comments, setComments] = useState(initial);
  const [composing, setComposing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState('');

  const post = async (text) => {
    setPosting(true);
    setPostError('');
    try {
      const res = await teacherAnalyticsAPI.postStudentComment(
        studentId, text, { subject_kind: item.kind, subject_id: item.id },
      );
      const comment = {
        id: res?.id || `local-${Date.now()}`,
        author_label: 'You',
        created_at: res?.created_at || new Date().toISOString(),
        text,
      };
      setComments((prev) => [comment, ...prev]);
      setComposing(false);
      if (onCommented) onCommented(item, comment);
      return true;
    } catch (err) {
      setPostError(isForbidden(err)
        ? 'You do not have access to comment on this student.'
        : 'Could not post your comment. Please try again.');
      return false;
    } finally {
      setPosting(false);
    }
  };

  const cancel = () => { setComposing(false); setPostError(''); };

  return {
    comments, composing, posting, postError, post, cancel, startComposing: () => setComposing(true),
  };
}
